import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import ApiBaseUrl from "../constants/apiUrl";
import { 
  Briefcase, 
  Plus, 
  Search, 
  Eye, 
  Edit3, 
  Trash2, 
  Users, 
  MapPin, 
  Clock, 
  DollarSign, 
  CheckCircle, 
  XCircle,
  AlertCircle,
  Layers,
  ChevronLeft,
  ChevronRight
} from "lucide-react";

const CareerJobsList = () => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({
    totalJobs: 0,
    totalActive: 0,
    totalClosed: 0,
    totalApplications: 0,
  });

  const [filters, setFilters] = useState({
    search: "",
    department: "All",
    status: "All",
    jobType: "All",
    page: 1,
    limit: 15,
  });

  const [totalPages, setTotalPages] = useState(1);
  const [totalJobs, setTotalJobs] = useState(0);
  const navigate = useNavigate();

  const departmentsList = [
    "All",
    "Engineering & IT",
    "Sales & Marketing",
    "Operations & Logistics",
    "Finance & Accounts",
    "Customer Support",
    "Human Resources",
    "Legal & Compliance",
    "Design & Creative",
  ];

  const fetchJobs = async () => {
    setLoading(true);
    setError(null);
    try {
      const authData = JSON.parse(localStorage.getItem("adminAuth"));
      const query = new URLSearchParams({
        page: filters.page,
        limit: filters.limit,
        ...(filters.search && { search: filters.search }),
        ...(filters.department !== "All" && { department: filters.department }),
        ...(filters.status !== "All" && { status: filters.status }),
        ...(filters.jobType !== "All" && { jobType: filters.jobType }),
      });

      const res = await fetch(`${ApiBaseUrl}/admin/career/jobs?${query}`, {
        headers: {
          Authorization: `Bearer ${authData?.token || ""}`,
          "Content-Type": "application/json",
        },
      });

      const data = await res.json();
      if (data.success) {
        setJobs(data.data.jobs || []);
        setTotalPages(data.data.totalPages || 1);
        setTotalJobs(data.data.total || 0);
        if (data.data.stats) {
          setStats(data.data.stats);
        }
      } else {
        setError(data.message || "Failed to load jobs");
      }
    } catch (err) {
      console.error("Error fetching jobs:", err);
      setError("Server connection failed. Could not load job listings.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchJobs();
  }, [filters.page, filters.department, filters.status, filters.jobType]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setFilters((prev) => ({ ...prev, page: 1 }));
    fetchJobs();
  };

  const handleToggleStatus = async (jobId, currentStatus) => {
    const nextStatus = currentStatus === "active" ? "inactive" : "active";
    try {
      const authData = JSON.parse(localStorage.getItem("adminAuth"));
      const res = await fetch(`${ApiBaseUrl}/admin/career/jobs/${jobId}/status`, {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${authData?.token || ""}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ status: nextStatus }),
      });

      const data = await res.json();
      if (data.success) {
        setJobs((prev) =>
          prev.map((j) => (j.jobId === jobId || j._id === jobId ? { ...j, status: nextStatus } : j))
        );
      } else {
        alert(data.message || "Failed to update status");
      }
    } catch (err) {
      console.error(err);
      alert("Error changing status");
    }
  };

  const handleDeleteJob = async (jobId, title) => {
    if (!window.confirm(`Are you sure you want to delete the job posting "${title}"?`)) {
      return;
    }

    try {
      const authData = JSON.parse(localStorage.getItem("adminAuth"));
      const res = await fetch(`${ApiBaseUrl}/admin/career/jobs/${jobId}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${authData?.token || ""}`,
        },
      });

      const data = await res.json();
      if (data.success) {
        setJobs((prev) => prev.filter((j) => j.jobId !== jobId && j._id !== jobId));
        setStats((prev) => ({ ...prev, totalJobs: Math.max(0, prev.totalJobs - 1) }));
      } else {
        alert(data.message || "Failed to delete job posting");
      }
    } catch (err) {
      console.error(err);
      alert("Error deleting job posting");
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-[1600px] mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div>
          <div className="flex items-center gap-2 text-sm text-gray-500 mb-1">
            <Link to="/" className="hover:text-red-600">Dashboard</Link>
            <span>/</span>
            <span className="text-gray-900 font-medium">Careers & Jobs</span>
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 flex items-center gap-3">
            <Briefcase className="w-8 h-8 text-red-600" />
            Job Postings Management
          </h1>
          <p className="text-sm text-gray-600 mt-1">
            Create, manage open vacancies, and review applicant flows across departments.
          </p>
        </div>

        <div className="flex items-center gap-3 flex-wrap">
          <Link
            to="/career/applications"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-gray-300 bg-white hover:bg-gray-50 text-gray-700 text-sm font-semibold shadow-sm transition"
          >
            <Users className="w-4 h-4 text-gray-500" />
            View Applications
          </Link>

          <Link
            to="/career/jobs/add"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-red-600 hover:bg-red-700 text-white text-sm font-semibold shadow-md hover:shadow-lg transition"
          >
            <Plus className="w-5 h-5" />
            Post New Job
          </Link>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <div className="bg-white p-5 rounded-2xl border border-gray-200 shadow-sm">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-500">Total Positions</span>
            <div className="w-10 h-10 rounded-xl bg-red-50 text-red-600 flex items-center justify-center">
              <Briefcase className="w-5 h-5" />
            </div>
          </div>
          <div className="mt-3">
            <span className="text-2xl font-bold text-gray-900">{stats.totalJobs}</span>
            <span className="text-xs text-gray-500 ml-2">All postings</span>
          </div>
        </div>

        <div className="bg-white p-5 rounded-2xl border border-gray-200 shadow-sm">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-500">Active Openings</span>
            <div className="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center">
              <CheckCircle className="w-5 h-5" />
            </div>
          </div>
          <div className="mt-3">
            <span className="text-2xl font-bold text-emerald-600">{stats.totalActive}</span>
            <span className="text-xs text-emerald-600/80 ml-2">Live on site</span>
          </div>
        </div>

        <div className="bg-white p-5 rounded-2xl border border-gray-200 shadow-sm">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-500">Closed / Inactive</span>
            <div className="w-10 h-10 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center">
              <XCircle className="w-5 h-5" />
            </div>
          </div>
          <div className="mt-3">
            <span className="text-2xl font-bold text-amber-600">{stats.totalClosed}</span>
            <span className="text-xs text-gray-500 ml-2">Hidden</span>
          </div>
        </div>

        <div className="bg-white p-5 rounded-2xl border border-gray-200 shadow-sm">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-500">Total Applicants</span>
            <div className="w-10 h-10 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center">
              <Users className="w-5 h-5" />
            </div>
          </div>
          <div className="mt-3">
            <span className="text-2xl font-bold text-blue-600">{stats.totalApplications}</span>
            <span className="text-xs text-gray-500 ml-2">Submissions</span>
          </div>
        </div>
      </div>

      {/* Filter and Search Bar */}
      <div className="bg-white p-4 rounded-2xl border border-gray-200 shadow-sm mb-6 space-y-4">
        <form onSubmit={handleSearchSubmit} className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="w-5 h-5 text-gray-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              placeholder="Search by job title, department, or location..."
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:bg-white"
            />
          </div>

          <div className="flex flex-wrap sm:flex-nowrap gap-2">
            <select
              value={filters.department}
              onChange={(e) => setFilters({ ...filters, department: e.target.value, page: 1 })}
              className="px-3.5 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm font-medium text-gray-700 focus:outline-none focus:ring-2 focus:ring-red-500"
            >
              {departmentsList.map((dep) => (
                <option key={dep} value={dep}>
                  {dep === "All" ? "All Departments" : dep}
                </option>
              ))}
            </select>

            <select
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value, page: 1 })}
              className="px-3.5 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm font-medium text-gray-700 focus:outline-none focus:ring-2 focus:ring-red-500"
            >
              <option value="All">All Statuses</option>
              <option value="active">Active Only</option>
              <option value="inactive">Inactive Only</option>
              <option value="closed">Closed Only</option>
            </select>

            <select
              value={filters.jobType}
              onChange={(e) => setFilters({ ...filters, jobType: e.target.value, page: 1 })}
              className="px-3.5 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm font-medium text-gray-700 focus:outline-none focus:ring-2 focus:ring-red-500"
            >
              <option value="All">All Job Types</option>
              <option value="Full-time">Full-time</option>
              <option value="Part-time">Part-time</option>
              <option value="Remote">Remote</option>
              <option value="Hybrid">Hybrid</option>
              <option value="Internship">Internship</option>
            </select>

            <button
              type="submit"
              className="px-5 py-2.5 bg-gray-900 hover:bg-gray-800 text-white rounded-xl text-sm font-medium transition"
            >
              Filter
            </button>
          </div>
        </form>
      </div>

      {/* Job Postings Table */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-12 text-center">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-red-600 mx-auto"></div>
            <p className="mt-3 text-sm text-gray-500">Loading career vacancies...</p>
          </div>
        ) : error ? (
          <div className="p-8 text-center text-red-600">
            <AlertCircle className="w-10 h-10 mx-auto mb-2 opacity-80" />
            <p className="font-semibold">{error}</p>
            <button
              onClick={fetchJobs}
              className="mt-3 px-4 py-2 bg-red-100 text-red-700 rounded-lg text-sm font-medium hover:bg-red-200"
            >
              Retry
            </button>
          </div>
        ) : jobs.length === 0 ? (
          <div className="p-16 text-center">
            <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-4 text-gray-400">
              <Briefcase className="w-8 h-8" />
            </div>
            <h3 className="text-lg font-bold text-gray-900 mb-1">No Job Postings Found</h3>
            <p className="text-sm text-gray-500 max-w-sm mx-auto mb-6">
              There are currently no job openings matching your search criteria.
            </p>
            <Link
              to="/career/jobs/add"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-red-600 text-white rounded-xl text-sm font-semibold hover:bg-red-700 transition"
            >
              <Plus className="w-4 h-4" />
              Post Your First Job
            </Link>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50/75 text-xs font-semibold uppercase text-gray-500 tracking-wider">
                  <th className="px-6 py-4">Job Title & ID</th>
                  <th className="px-6 py-4">Department</th>
                  <th className="px-6 py-4">Type & Workplace</th>
                  <th className="px-6 py-4">Location</th>
                  <th className="px-6 py-4 text-center">Applications</th>
                  <th className="px-6 py-4 text-center">Status</th>
                  <th className="px-6 py-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 text-sm">
                {jobs.map((job) => (
                  <tr key={job._id || job.jobId} className="hover:bg-gray-50/80 transition">
                    <td className="px-6 py-4">
                      <div>
                        <div className="font-semibold text-gray-900 flex items-center gap-2">
                          {job.title}
                          {job.featured && (
                            <span className="px-2 py-0.5 text-[10px] font-bold bg-amber-100 text-amber-800 rounded-full">
                              FEATURED
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-gray-400 font-mono mt-0.5">
                          ID: {job.jobId} | Exp: {job.experienceLevel}
                        </div>
                      </div>
                    </td>

                    <td className="px-6 py-4">
                      <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-medium bg-gray-100 text-gray-800 border border-gray-200">
                        <Layers className="w-3.5 h-3.5 text-gray-500" />
                        {job.department}
                      </span>
                    </td>

                    <td className="px-6 py-4">
                      <div className="text-gray-900 font-medium">{job.jobType}</div>
                      <div className="text-xs text-gray-500">{job.workplaceType || "On-site"}</div>
                    </td>

                    <td className="px-6 py-4">
                      <div className="flex items-center gap-1.5 text-gray-600">
                        <MapPin className="w-4 h-4 text-gray-400 shrink-0" />
                        <span>{job.location}</span>
                      </div>
                    </td>

                    <td className="px-6 py-4 text-center">
                      <Link
                        to={`/career/applications?jobId=${job.jobId}`}
                        className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold bg-blue-50 text-blue-700 hover:bg-blue-100 transition"
                      >
                        <Users className="w-3.5 h-3.5" />
                        {job.totalApplications || 0}
                      </Link>
                    </td>

                    <td className="px-6 py-4 text-center">
                      <button
                        onClick={() => handleToggleStatus(job.jobId, job.status)}
                        className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold cursor-pointer transition ${
                          job.status === "active"
                            ? "bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100"
                            : "bg-gray-100 text-gray-600 border border-gray-200 hover:bg-gray-200"
                        }`}
                        title="Click to toggle status"
                      >
                        <span
                          className={`w-1.5 h-1.5 rounded-full ${
                            job.status === "active" ? "bg-emerald-500" : "bg-gray-400"
                          }`}
                        />
                        {job.status === "active" ? "Active" : "Inactive"}
                      </button>
                    </td>

                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Link
                          to={`/career/applications?jobId=${job.jobId}`}
                          className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition"
                          title="View Applicants"
                        >
                          <Users className="w-4 h-4" />
                        </Link>

                        <Link
                          to={`/career/jobs/edit/${job.jobId || job._id}`}
                          className="p-2 text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition"
                          title="Edit Job"
                        >
                          <Edit3 className="w-4 h-4" />
                        </Link>

                        <button
                          onClick={() => handleDeleteJob(job.jobId || job._id, job.title)}
                          className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition"
                          title="Delete Job"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
            <span className="text-xs text-gray-500">
              Showing page <strong className="text-gray-800">{filters.page}</strong> of{" "}
              <strong className="text-gray-800">{totalPages}</strong> (Total {totalJobs} jobs)
            </span>
            <div className="flex gap-2">
              <button
                disabled={filters.page <= 1}
                onClick={() => setFilters((prev) => ({ ...prev, page: prev.page - 1 }))}
                className="p-2 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button
                disabled={filters.page >= totalPages}
                onClick={() => setFilters((prev) => ({ ...prev, page: prev.page + 1 }))}
                className="p-2 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CareerJobsList;
